/**
 * Hariom ERP - Demand Forecasting Module
 *
 * Core feature: Tracks each contractor's monthly committed tonnage (Forecast)
 * vs actual purchased tonnage (Actual). Auto-calculates Diff = Forecast - Actual.
 *
 * Columns mirror the Excel: Contractor | Total | Status | Mar Forecast | Mar Actual | Mar Diff | Apr...
 * Red diff = contractor under-performing → marketing team follows up
 * "NEED TO TALK" status = flagged for immediate sales follow-up
 *
 * Designed to replace the Excel sheet the founder currently uses.
 */
import React, { useState, useMemo, useRef, useEffect } from 'react'
import {
  Search, Plus, Download, Upload, Filter, TrendingDown,
  AlertTriangle, CheckCircle, Edit3, Trash2, Eye,
  ChevronDown, ChevronUp, RefreshCw, MessageSquare, Check, X
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, Cell
} from 'recharts'
import clsx from 'clsx'
// SheetJS — reads/writes .xlsx entirely in the browser (no backend needed).
// This is what powers the "Import" (from PC) and "Export Excel" buttons.
import * as XLSX from 'xlsx'
// Lightweight toast notifications (the <Toaster/> is already mounted in App.tsx).
import toast from 'react-hot-toast'
import type { Contractor, ContractorStatus, MonthlyData } from '../../types'
// Global, persisted contractor list (survives page navigation + refresh).
import { useForecastStore } from '../../store/forecastStore'
// REAL company data (110 contractors auto-generated from the founder's Excel).
import { SEED_CONTRACTORS, SEED_VERSION } from '../../data/contractorsSeed'

// ===== MONTHS SHOWN IN TABLE =====
const MONTHS = ['MARCH', 'APR', 'MAY', 'JUNE', 'JULY', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC', 'JAN', 'FEB']

// ===== MOCK DATA — mirrors the uploaded Excel =====
const MOCK_CONTRACTORS: Contractor[] = [
  { id: '1',  srNo: 1,  name: 'SP SINGLA',              totalTon: 2000, status: 'active',       notes: '',
    monthlyData: [
      { month: 'MARCH', forecast: 250, actual: 171, diff: -79  },
      { month: 'APR',   forecast: 235, actual: 0,   diff: -235 },
      ...MONTHS.slice(2).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 485, totalActual: 171, totalDiff: -314,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '2',  srNo: 2,  name: 'J KUMAR',                totalTon: 0,    status: 'active',       notes: '',
    monthlyData: [
      { month: 'MARCH', forecast: 100, actual: 0,  diff: -100 },
      { month: 'APR',   forecast: 15,  actual: 0,  diff: -15  },
      ...MONTHS.slice(2).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 115, totalActual: 0, totalDiff: -115,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '3',  srNo: 3,  name: 'NCC',                    totalTon: 0,    status: 'active',       notes: '',
    monthlyData: [
      { month: 'MARCH', forecast: 50, actual: 0,  diff: -50 },
      ...MONTHS.slice(1).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 50, totalActual: 0, totalDiff: -50,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '4',  srNo: 4,  name: 'Heena/Ganga/Kuber',      totalTon: 0,    status: 'active',       notes: '',
    monthlyData: [
      { month: 'MARCH', forecast: 0,   actual: 0,   diff: 0   },
      { month: 'APR',   forecast: 0,   actual: 0,   diff: 0   },
      { month: 'MAY',   forecast: 153, actual: 0,   diff: -153},
      ...MONTHS.slice(3).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 153, totalActual: 0, totalDiff: -153,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '5',  srNo: 5,  name: 'MVR Panvel',              totalTon: 0,    status: 'active',       notes: '',
    monthlyData: [
      { month: 'MARCH', forecast: 30, actual: 0, diff: -30 },
      ...MONTHS.slice(1).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 30, totalActual: 0, totalDiff: -30,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '6',  srNo: 6,  name: 'VMC/Supreme',             totalTon: 0,    status: 'active',       notes: '',
    monthlyData: [
      { month: 'MARCH', forecast: 250, actual: 159, diff: -91 },
      ...MONTHS.slice(1).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 250, totalActual: 159, totalDiff: -91,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '7',  srNo: 7,  name: 'SUCHINTAN/Bhawani',       totalTon: 0,    status: 'active',       notes: '',
    monthlyData: [
      { month: 'MARCH', forecast: 60, actual: 60, diff: 0 },
      ...MONTHS.slice(1).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 60, totalActual: 60, totalDiff: 0,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '8',  srNo: 8,  name: 'SAHAJ/ZHUZOOR',           totalTon: 0,    status: 'active',       notes: '',
    monthlyData: [
      { month: 'MARCH', forecast: 30, actual: 40, diff: 10  },
      { month: 'APR',   forecast: 40, actual: 0,  diff: -40 },
      ...MONTHS.slice(2).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 70, totalActual: 40, totalDiff: -30,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '9',  srNo: 9,  name: 'LNT DBLR',                totalTon: 0,    status: 'active',       notes: '',
    monthlyData: [
      { month: 'MARCH', forecast: 100, actual: 0, diff: -100 },
      ...MONTHS.slice(1).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 100, totalActual: 0, totalDiff: -100,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '10', srNo: 10, name: 'TNT/RKC',                 totalTon: 0,    status: 'need_to_talk', notes: 'NEED TO TALK',
    monthlyData: [
      { month: 'MARCH', forecast: 250, actual: 31,  diff: -219 },
      { month: 'APR',   forecast: 212, actual: 0,   diff: -212 },
      { month: 'MAY',   forecast: 294, actual: 0,   diff: -294 },
      ...MONTHS.slice(3).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 756, totalActual: 31, totalDiff: -725,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '11', srNo: 11, name: 'KALPANA/sanket',           totalTon: 0,    status: 'active',       notes: '',
    monthlyData: [
      { month: 'MARCH', forecast: 0,  actual: 21, diff: 21 },
      ...MONTHS.slice(1).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 0, totalActual: 21, totalDiff: 21,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '12', srNo: 12, name: 'NCC SMC SATIS',            totalTon: 0,    status: 'need_to_talk', notes: 'NEED TO TALK',
    monthlyData: [
      { month: 'MARCH', forecast: 50, actual: 0, diff: -50 },
      ...MONTHS.slice(1).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 50, totalActual: 0, totalDiff: -50,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '13', srNo: 13, name: 'SIDHARTH/Chichani',        totalTon: 0,    status: 'need_to_talk', notes: 'NEED TO TALK',
    monthlyData: [
      { month: 'MARCH', forecast: 30, actual: 0, diff: -30 },
      ...MONTHS.slice(1).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 30, totalActual: 0, totalDiff: -30,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '14', srNo: 14, name: 'PATEL/Viswanath',          totalTon: 0,    status: 'active',       notes: '',
    monthlyData: [
      { month: 'MARCH', forecast: 150, actual: 34, diff: -116 },
      ...MONTHS.slice(1).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 150, totalActual: 34, totalDiff: -116,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
  { id: '15', srNo: 15, name: 'GPT/RE Infra',             totalTon: 0,    status: 'active',       notes: '',
    monthlyData: [
      { month: 'MARCH', forecast: 60,  actual: 61,  diff: 1   },
      { month: 'APR',   forecast: 269, actual: 0,   diff: -269},
      { month: 'MAY',   forecast: 233, actual: 0,   diff: -233},
      ...MONTHS.slice(3).map(m => ({ month: m, forecast: 0, actual: 0, diff: 0 }))
    ],
    totalForecast: 562, totalActual: 61, totalDiff: -501,
    createdAt: '2026-01-01', updatedAt: '2026-06-01'
  },
]

// ===== STATUS CONFIG =====
const STATUS_CONFIG: Record<ContractorStatus, { label: string; className: string }> = {
  active:       { label: 'Active',        className: 'badge-success' },
  need_to_talk: { label: 'Need to Talk',  className: 'badge-danger' },
  inactive:     { label: 'Inactive',      className: 'bg-steel-100 text-steel-600 text-xs px-2 py-0.5 rounded-full font-medium' },
  new:          { label: 'New',           className: 'badge-info' },
}

// ===== MAIN COMPONENT =====
const DemandForecast: React.FC = () => {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedYear] = useState(2026)
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table')
  const [expandedRow, setExpandedRow] = useState<string | null>(null)
  const [selectedMonth, setSelectedMonth] = useState('MARCH')

  // Contractor list now lives in a PERSISTED global store (see forecastStore.ts)
  // so navigating away and back — or refreshing — keeps every imported/added row.
  const { contractors, setContractors, addContractor, updateContractor } =
    useForecastStore()

  // Load the REAL company data (110 contractors from the founder's Excel) ONLY
  // on a genuine first run: we wait until the store has finished loading from the
  // server (it loads asynchronously), then seed ONLY if the list is still empty.
  // We never overwrite existing rows, so employee-entered data can never be wiped
  // by a re-seed — even if a sync momentarily hiccups.
  useEffect(() => {
    const seedIfEmpty = () => {
      const s = useForecastStore.getState()
      if (s.contractors.length === 0 && s.seedVersion !== SEED_VERSION) {
        s.setContractors(SEED_CONTRACTORS)
        s.setSeedVersion(SEED_VERSION)
      }
    }
    if (useForecastStore.persist.hasHydrated()) seedIfEmpty()
    return useForecastStore.persist.onFinishHydration(seedIfEmpty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Hidden <input type="file"> we trigger programmatically from the Import button.
  const fileInputRef = useRef<HTMLInputElement>(null)

  // ----- Inline row editing (pencil icon) -----
  const [editingId, setEditingId] = useState<string | null>(null)
  // Draft holds a { forecast, actual } string pair for EVERY month while editing
  // one row, so months outside the visible window keep their values on save.
  const [editDraft, setEditDraft] = useState<Record<string, { forecast: string; actual: string }>>({})
  // Row meta (name / Total(T) / status) edited alongside the month cells.
  const [editMeta, setEditMeta] = useState<{ name: string; totalTon: string; status: ContractorStatus }>({
    name: '', totalTon: '', status: 'active',
  })

  // ----- Bulk "Edit Table" mode (unlock EVERY row + month at once) -----
  const [bulkEdit, setBulkEdit] = useState(false)
  // bulkDraft[contractorId][month] = { forecast, actual }
  const [bulkDraft, setBulkDraft] =
    useState<Record<string, Record<string, { forecast: string; actual: string }>>>({})

  // ----- Add Contractor modal -----
  const emptyForm = {
    name: '', totalTon: '', status: 'active' as ContractorStatus,
    month: 'MARCH', forecast: '', actual: '',
  }
  const [showAddModal, setShowAddModal] = useState(false)
  const [addForm, setAddForm] = useState(emptyForm)

  // ===== FILTERED DATA =====
  const filteredContractors = useMemo(() => {
    return contractors.filter(c => {
      const matchSearch = c.name.toLowerCase().includes(search.toLowerCase())
      const matchStatus = statusFilter === 'all' || c.status === statusFilter
      return matchSearch && matchStatus
    })
  }, [search, statusFilter, contractors])

  // ===== SUMMARY TOTALS (for footer row) =====
  const totals = useMemo(() => {
    return filteredContractors.reduce((acc, c) => ({
      forecast: acc.forecast + c.totalForecast,
      actual:   acc.actual   + c.totalActual,
      diff:     acc.diff     + c.totalDiff,
    }), { forecast: 0, actual: 0, diff: 0 })
  }, [filteredContractors])

  // ===== MONTH-BASED TOTALS (MTD) =====
  // Sum a SINGLE month's forecast/actual across the filtered contractors.
  const monthAgg = (month: string) =>
    filteredContractors.reduce(
      (acc, c) => {
        const md = c.monthlyData.find(m => m.month === month)
        return { forecast: acc.forecast + (md?.forecast || 0), actual: acc.actual + (md?.actual || 0) }
      },
      { forecast: 0, actual: 0 }
    )
  // Current (selected) month → drives the "Total Forecast/Actual (MTD)" cards.
  const mtd = monthAgg(selectedMonth)
  const mtdGap = mtd.actual - mtd.forecast
  // Previous month (e.g. filter = MAY → APR) → drives the "Last Month" card.
  const prevMonth = MONTHS.indexOf(selectedMonth) > 0 ? MONTHS[MONTHS.indexOf(selectedMonth) - 1] : null
  const lastAgg = prevMonth ? monthAgg(prevMonth) : null

  // ===== CHART DATA =====
  const chartData = filteredContractors
    .filter(c => c.totalForecast > 0)
    .sort((a, b) => a.totalDiff - b.totalDiff)
    .slice(0, 10)
    .map(c => ({
      name: c.name.length > 15 ? c.name.slice(0, 15) + '…' : c.name,
      forecast: c.totalForecast,
      actual: c.totalActual,
      gap: Math.abs(c.totalDiff),
    }))

  // ===== NEED TO TALK count =====
  const needToTalkCount = contractors.filter(c => c.status === 'need_to_talk').length

  // ===== VISIBLE MONTH WINDOW =====
  // The table shows a sliding window of 6 months that follows the month filter,
  // so picking AUG / SEP / OCT etc. actually scrolls those columns into view
  // (previously only the first 6 months were ever shown).
  const VISIBLE_MONTHS = 6
  const startIdx = Math.min(
    Math.max(0, MONTHS.indexOf(selectedMonth)),
    MONTHS.length - VISIBLE_MONTHS
  )
  const visibleMonths = MONTHS.slice(startIdx, startIdx + VISIBLE_MONTHS)

  /* =====================================================================
   *  EXCEL EXPORT  →  "Export Excel" button
   * ---------------------------------------------------------------------
   *  Writes the current contractor list to a .xlsx file and downloads it
   *  to the user's PC. One row per contractor; for every month we emit
   *  Forecast / Actual / Diff columns so the sheet mirrors the table.
   *  This same layout is what the Import below understands, so a user can
   *  Export → edit in Excel → Import again with no data loss.
   * ===================================================================== */
  const handleExportExcel = () => {
    try {
      const rows = contractors.map(c => {
        // Fixed leading columns
        const row: Record<string, string | number> = {
          'Sr No': c.srNo,
          'Contractor': c.name,
          'Total Ton': c.totalTon,
          'Status': STATUS_CONFIG[c.status]?.label ?? c.status,
        }
        // One Forecast / Actual / Diff trio per month
        MONTHS.forEach(m => {
          const md = c.monthlyData.find(x => x.month === m)
          row[`${m} Forecast`] = md?.forecast ?? 0
          row[`${m} Actual`]   = md?.actual ?? 0
          row[`${m} Diff`]     = md?.diff ?? 0
        })
        row['Notes'] = c.notes ?? ''
        return row
      })

      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Demand Forecast')
      const fileName = `Hariom_Demand_Forecast_FY${selectedYear}_${new Date().toISOString().slice(0, 10)}.xlsx`
      XLSX.writeFile(wb, fileName)
      toast.success(`Exported ${contractors.length} contractors to Excel`)
    } catch (err) {
      console.error('Excel export failed:', err)
      toast.error('Could not export the Excel file')
    }
  }

  /* =====================================================================
   *  EXCEL IMPORT  →  "Import" button (file picked from the PC)
   * ---------------------------------------------------------------------
   *  Reads the chosen .xlsx in the browser, maps each row to a Contractor,
   *  RE-CALCULATES every Diff (= Actual − Forecast) and all the YTD totals,
   *  then replaces the table data so the analysis updates in real time.
   *  Header matching is deliberately tolerant: column names are compared
   *  case-insensitively and we accept "Forecast" or "Fcst", full month
   *  names or 3-letter month codes, etc.
   * ===================================================================== */

  // Map any free-text status from Excel onto our ContractorStatus union.
  const parseStatus = (raw: unknown): ContractorStatus => {
    const s = String(raw ?? '').trim().toLowerCase()
    if (s.includes('need')) return 'need_to_talk'   // "NEED TO TALK"
    if (s === 'inactive') return 'inactive'
    if (s === 'new') return 'new'
    return 'active'
  }

  // Safely coerce any cell value (string/number/blank) to a number.
  const toNum = (v: unknown): number => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0
    const n = parseFloat(String(v ?? '').replace(/[^0-9.\-]/g, ''))
    return Number.isFinite(n) ? n : 0
  }

  // Normalize a header cell: lowercase + single-spaced + trimmed.
  const normCell = (v: unknown): string =>
    String(v ?? '').toLowerCase().replace(/\s+/g, ' ').trim()

  // Match a cell to one of our MONTHS via its 3-letter prefix, so "MARCH",
  // "Mar", "APRIL", "Apr", "JUNE", "Jun" all resolve to the right month key.
  const monthToken = (s: string): string | null => {
    for (const m of MONTHS) {
      if (s.startsWith(m.slice(0, 3).toLowerCase())) return m
    }
    return null
  }

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const buffer = await file.arrayBuffer()
      const wb = XLSX.read(buffer, { type: 'array' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      // Read the sheet as a raw grid (array-of-arrays) so WE choose the header
      // row. Real sheets have a title row on top and a two-row month header
      // (month names on one row, Forecast/Actual/Diff on the next), so we
      // cannot assume row 0 is the header.
      const grid = XLSX.utils.sheet_to_json<unknown[]>(ws, {
        header: 1, defval: '', raw: true,
      })

      if (!grid.length) {
        toast.error('That Excel sheet has no rows')
        return
      }

      // 1) Find the header row = first row (in the top 25) containing a
      //    "Contractor" / "Party" / "Name" cell.
      let headerRow = -1
      for (let r = 0; r < Math.min(grid.length, 25); r++) {
        const row = grid[r] as unknown[]
        if (row.some(cell =>
          ['contractor', 'contractor name', 'party', 'party name', 'name'].includes(normCell(cell)))) {
          headerRow = r
          break
        }
      }
      if (headerRow === -1) {
        toast.error('Could not find a "Contractor" column in this sheet')
        return
      }

      const header = (grid[headerRow] as unknown[]).map(normCell)
      // Month names often live in the row ABOVE the sub-headers (merged cells).
      const monthRow = headerRow > 0 ? (grid[headerRow - 1] as unknown[]).map(normCell) : []

      // 2) Locate the fixed (non-month) columns by their header text.
      const findCol = (pred: (h: string) => boolean) => header.findIndex(pred)
      const isOneOf = (...names: string[]) => (h: string) => names.includes(h)
      const colName   = findCol(isOneOf('contractor', 'contractor name', 'party', 'party name', 'name'))
      const colSrNo   = findCol(isOneOf('sr no', 'srno', 'sr', 's no', '#'))
      const colStatus = findCol(h => h === 'status' || h.includes('status'))
      const colNotes  = findCol(h => ['notes', 'remark', 'remarks'].includes(h) || h.includes('remark'))
      const colTon    = findCol(isOneOf('ton', 'total ton', 'totalton', 'total (t)', 'annual', 'commitment'))
      const colTotal  = findCol(isOneOf('total'))
      const fixed = new Set([colName, colSrNo, colStatus, colNotes, colTon, colTotal].filter(c => c >= 0))

      // 3) Map each month to its Forecast & Actual columns. Walk left→right;
      //    the month name may come from the row above (two-row layout) OR from
      //    the header cell itself ("MARCH Forecast" — our own export format).
      const monthCols: Record<string, { f?: number; a?: number }> = {}
      let currentMonth: string | null = null
      for (let c = 0; c < header.length; c++) {
        if (fixed.has(c)) continue
        const h = header[c]
        const above = monthRow[c] || ''
        const mAbove = monthToken(above)
        const mHead = monthToken(h)
        if (mAbove) currentMonth = mAbove
        if (mHead) currentMonth = mHead
        if (!currentMonth) continue

        let kind: 'forecast' | 'actual' | 'diff' | null = null
        if (h.includes('actual')) kind = 'actual'
        else if (h.includes('diff')) kind = 'diff'
        else if (h.includes('forecast') || h.includes('forcast') || h.includes('foracst') ||
                 h.includes('fcst') || h.startsWith('for')) kind = 'forecast'
        else if (mAbove || mHead) kind = 'forecast'   // first (often blank) cell of a trio

        if (kind === 'forecast') monthCols[currentMonth] = { ...monthCols[currentMonth], f: c }
        else if (kind === 'actual') monthCols[currentMonth] = { ...monthCols[currentMonth], a: c }
        // "diff" columns are ignored — Diff is always recomputed (Actual − Forecast).
      }

      // 4) Read every contractor row below the header.
      const imported: Contractor[] = []
      for (let r = headerRow + 1; r < grid.length; r++) {
        const row = grid[r] as unknown[]
        const name = colName >= 0 ? String(row[colName] ?? '').trim() : ''
        if (!name) continue                                            // blank / totals rows
        if (['contractor', 'total', 'grand total', 'sr no'].includes(name.toLowerCase())) continue

        const monthlyData: MonthlyData[] = MONTHS.map(m => {
          const mc = monthCols[m] || {}
          const forecast = mc.f != null ? toNum(row[mc.f]) : 0
          const actual = mc.a != null ? toNum(row[mc.a]) : 0
          // Diff = Actual − Forecast → negative = shortfall (red, follow-up).
          return { month: m, forecast, actual, diff: actual - forecast }
        })
        const totalForecast = monthlyData.reduce((s, m) => s + m.forecast, 0)
        const totalActual = monthlyData.reduce((s, m) => s + m.actual, 0)

        imported.push({
          id: `imp-${Date.now()}-${r}`,
          srNo: colSrNo >= 0 ? (toNum(row[colSrNo]) || imported.length + 1) : imported.length + 1,
          name,
          totalTon: colTon >= 0 ? toNum(row[colTon]) : (colTotal >= 0 ? toNum(row[colTotal]) : 0),
          status: colStatus >= 0 ? parseStatus(row[colStatus]) : 'active',
          monthlyData,
          totalForecast,
          totalActual,
          totalDiff: totalActual - totalForecast,
          notes: colNotes >= 0 ? String(row[colNotes] ?? '').trim() : '',
          createdAt: new Date().toISOString().slice(0, 10),
          updatedAt: new Date().toISOString().slice(0, 10),
        })
      }

      if (!imported.length) {
        toast.error('Found the header, but no contractor rows below it')
        return
      }

      setContractors(imported)              // ← table, charts & KPIs refresh instantly
      setSearch('')
      setStatusFilter('all')
      toast.success(`Imported ${imported.length} contractors from ${file.name}`)
    } catch (err) {
      console.error('Excel import failed:', err)
      toast.error('Could not read this file. Please upload a valid .xlsx / .xls file.')
    } finally {
      // Reset the input so picking the SAME file again still fires onChange.
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  /* =====================================================================
   *  ADD CONTRACTOR  →  "Add Contractor" button (opens the modal form)
   *  Builds one Contractor from the form and appends it to the live table.
   * ===================================================================== */
  const handleAddContractor = () => {
    const name = addForm.name.trim()
    if (!name) {
      toast.error('Please enter a contractor name')
      return
    }
    // Block duplicates: the same contractor name (case-insensitive) can't be
    // added twice — show a red error instead.
    if (contractors.some(c => c.name.trim().toLowerCase() === name.toLowerCase())) {
      toast.error(`"${name}" already exists in the list`)
      return
    }
    const f = toNum(addForm.forecast)
    const a = toNum(addForm.actual)
    const monthlyData: MonthlyData[] = MONTHS.map(m =>
      m === addForm.month
        ? { month: m, forecast: f, actual: a, diff: a - f }
        : { month: m, forecast: 0, actual: 0, diff: 0 }
    )
    const newC: Contractor = {
      id: `new-${Date.now()}`,
      srNo: contractors.length + 1,
      name,
      totalTon: toNum(addForm.totalTon),
      status: addForm.status,
      monthlyData,
      totalForecast: f,
      totalActual: a,
      totalDiff: a - f,
      notes: addForm.status === 'need_to_talk' ? 'NEED TO TALK' : '',
      createdAt: new Date().toISOString().slice(0, 10),
      updatedAt: new Date().toISOString().slice(0, 10),
    }
    addContractor(newC)   // persisted store → table/charts/KPIs update instantly
    setShowAddModal(false)
    setAddForm(emptyForm)
    toast.success(`Added contractor "${name}"`)
  }

  /* =====================================================================
   *  INLINE EDIT  →  pencil icon on each row
   *  Click pencil → the row's Forecast/Actual cells (for the months on
   *  screen) become inputs; Diff recalculates live as you type. Save writes
   *  it back to the store (totals recomputed); Cancel discards.
   * ===================================================================== */
  const startEdit = (c: Contractor) => {
    const draft: Record<string, { forecast: string; actual: string }> = {}
    MONTHS.forEach(m => {
      const md = c.monthlyData.find(x => x.month === m)
      draft[m] = {
        forecast: md && md.forecast ? String(md.forecast) : '',
        actual: md && md.actual ? String(md.actual) : '',
      }
    })
    setEditDraft(draft)
    setEditMeta({ name: c.name, totalTon: c.totalTon ? String(c.totalTon) : '', status: c.status })
    setEditingId(c.id)
    setExpandedRow(null)   // close any open chart row to avoid clashing
  }

  // AUTO STATUS FROM DATA — used when the user hasn't manually picked a status:
  //   • actual ≥ forecast (commitment met/exceeded) → active
  //   • big shortfall (actual < 50% of forecast)     → need_to_talk
  //   • otherwise keep the current status
  const deriveStatus = (current: ContractorStatus, tf: number, ta: number): ContractorStatus => {
    if (tf > 0 && ta >= tf) return 'active'
    if (tf > 0 && ta < tf * 0.5) return 'need_to_talk'
    return current
  }

  // Permanently remove a contractor from the software.
  const deleteContractor = (c: Contractor) => {
    if (window.confirm(`Delete contractor "${c.name}"? This removes it permanently.`)) {
      setContractors(contractors.filter(x => x.id !== c.id))
      if (editingId === c.id) setEditingId(null)
      toast.success(`Deleted ${c.name}`)
    }
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditDraft({})
  }

  const saveEdit = (c: Contractor) => {
    const monthlyData: MonthlyData[] = MONTHS.map(m => {
      const d = editDraft[m] || { forecast: '', actual: '' }
      const forecast = toNum(d.forecast)
      const actual = toNum(d.actual)
      return { month: m, forecast, actual, diff: actual - forecast }
    })
    const totalForecast = monthlyData.reduce((s, m) => s + m.forecast, 0)
    const totalActual = monthlyData.reduce((s, m) => s + m.actual, 0)

    // STATUS: if the user changed it in the dropdown, respect that choice;
    // otherwise auto-derive it from the new data (see deriveStatus above).
    const status: ContractorStatus =
      editMeta.status !== c.status ? editMeta.status : deriveStatus(c.status, totalForecast, totalActual)
    let notes = c.notes
    if (status !== 'need_to_talk' && notes === 'NEED TO TALK') notes = ''
    if (status === 'need_to_talk' && !notes) notes = 'NEED TO TALK'

    const newName = editMeta.name.trim() || c.name
    updateContractor(c.id, {
      ...c,
      name: newName,
      totalTon: toNum(editMeta.totalTon),
      monthlyData,
      totalForecast,
      totalActual,
      totalDiff: totalActual - totalForecast,
      status,
      notes,
      updatedAt: new Date().toISOString().slice(0, 10),
    })
    setEditingId(null)
    setEditDraft({})
    toast.success(`Updated ${newName}`)
  }

  // Helper used by the inline inputs to update one field of the draft.
  const setDraftField = (month: string, field: 'forecast' | 'actual', value: string) => {
    setEditDraft(prev => ({
      ...prev,
      [month]: { ...(prev[month] || { forecast: '', actual: '' }), [field]: value },
    }))
  }

  /* =====================================================================
   *  BULK EDIT  →  the "Edit Table" button above the table
   *  Unlocks Forecast/Actual for EVERY contractor and EVERY month at once so
   *  the marketing team can fill the whole table quickly, then Save (one click,
   *  success toast) or Cancel. Diff recalculates live. Per-row pencil still works.
   * ===================================================================== */
  const startBulkEdit = () => {
    const draft: Record<string, Record<string, { forecast: string; actual: string }>> = {}
    contractors.forEach(c => {
      const rd: Record<string, { forecast: string; actual: string }> = {}
      MONTHS.forEach(m => {
        const md = c.monthlyData.find(x => x.month === m)
        rd[m] = {
          forecast: md && md.forecast ? String(md.forecast) : '',
          actual: md && md.actual ? String(md.actual) : '',
        }
      })
      draft[c.id] = rd
    })
    setBulkDraft(draft)
    setBulkEdit(true)
    setEditingId(null)     // leave any single-row edit
    setExpandedRow(null)
  }

  const cancelBulk = () => {
    setBulkEdit(false)
    setBulkDraft({})
  }

  const saveBulk = () => {
    const updated = contractors.map(c => {
      const rd = bulkDraft[c.id]
      if (!rd) return c
      const monthlyData: MonthlyData[] = MONTHS.map(m => {
        const d = rd[m] || { forecast: '', actual: '' }
        const forecast = toNum(d.forecast)
        const actual = toNum(d.actual)
        return { month: m, forecast, actual, diff: actual - forecast }
      })
      const totalForecast = monthlyData.reduce((s, m) => s + m.forecast, 0)
      const totalActual = monthlyData.reduce((s, m) => s + m.actual, 0)
      // Auto-clear "Need to Talk" when a contractor now meets its commitment.
      let status = c.status
      let notes = c.notes
      if (c.status === 'need_to_talk' && totalForecast > 0 && totalActual >= totalForecast) {
        status = 'active'
        if (notes === 'NEED TO TALK') notes = ''
      }
      return {
        ...c, monthlyData, totalForecast, totalActual,
        totalDiff: totalActual - totalForecast, status, notes,
        updatedAt: new Date().toISOString().slice(0, 10),
      }
    })
    setContractors(updated)
    setBulkEdit(false)
    setBulkDraft({})
    toast.success('All changes saved successfully')
  }

  // Update one field of one cell while in bulk-edit mode.
  const setBulkDraftField = (cid: string, month: string, field: 'forecast' | 'actual', value: string) => {
    setBulkDraft(prev => {
      const row: Record<string, { forecast: string; actual: string }> = prev[cid] || {}
      const cell = row[month] || { forecast: '', actual: '' }
      return { ...prev, [cid]: { ...row, [month]: { ...cell, [field]: value } } }
    })
  }

  return (
    <div className="space-y-5">

      {/* ===== PAGE HEADER ===== */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Demand Forecasting</h1>
          <p className="page-subtitle">
            FY {selectedYear} — Contractor commitment tracking &amp; monthly offtake analysis
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export Excel button — downloads current table as .xlsx */}
          <button
            onClick={handleExportExcel}
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <Download size={15} /> Export Excel
          </button>
          {/* Import Excel button — opens the file picker (hidden input below) */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <Upload size={15} /> Import
          </button>
          {/* Hidden file input that actually receives the chosen Excel file */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleImportExcel}
            className="hidden"
          />
          {/* Add new contractor */}
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <Plus size={15} /> Add Contractor
          </button>
        </div>
      </div>

      {/* ===== SUMMARY STAT CARDS ===== */}
      {/* 5 boards. Cards 3 & 4 are month-based (MTD) and follow the month
          filter; card 5 shows the previous month's summary. */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {/* 1 — Total Contractors (live count, grows as you add) */}
        <div className="card border-l-4 border-l-primary-500 py-3">
          <p className="text-xs text-steel-500">Total Contractors</p>
          <p className="text-2xl font-bold text-steel-900">{contractors.length}</p>
        </div>

        {/* 2 — Need to Talk (auto-clears when a contractor meets commitment) */}
        <div className="card border-l-4 border-l-red-500 py-3">
          <p className="text-xs text-steel-500">Need to Talk</p>
          <p className="text-2xl font-bold text-red-600">{needToTalkCount}</p>
          <p className="text-xs text-red-500">Immediate follow-up required</p>
        </div>

        {/* 3 — Total Forecast for the SELECTED month (MTD) */}
        <div className="card border-l-4 border-l-blue-500 py-3">
          <p className="text-xs text-steel-500">Total Forecast (MTD)</p>
          <p className="text-2xl font-bold text-steel-900">
            {mtd.forecast.toLocaleString()} T
          </p>
          <p className="text-xs text-blue-500 font-medium">{selectedMonth}</p>
        </div>

        {/* 4 — Total Actual for the SELECTED month (MTD) + gap */}
        <div className="card border-l-4 border-l-green-500 py-3">
          <p className="text-xs text-steel-500">Total Actual (MTD)</p>
          <p className="text-2xl font-bold text-steel-900">
            {mtd.actual.toLocaleString()} T
          </p>
          <p className={clsx('text-xs', mtdGap < 0 ? 'text-red-500' : 'text-green-600')}>
            Gap: {Math.abs(mtdGap)}T {mtdGap < 0 ? '▼ under' : '▲ over'}
          </p>
        </div>

        {/* 5 — Previous month summary (e.g. filter = MAY → shows APR) */}
        <div className="card border-l-4 border-l-amber-500 py-3">
          <p className="text-xs text-steel-500">
            Last Month {prevMonth ? `(${prevMonth})` : ''}
          </p>
          {prevMonth && lastAgg ? (
            <div className="mt-0.5 text-xs leading-snug">
              <p className="text-steel-600">Forecast: <span className="font-semibold text-steel-800">{lastAgg.forecast.toLocaleString()}</span> T</p>
              <p className="text-steel-600">Actual: <span className="font-semibold text-steel-800">{lastAgg.actual.toLocaleString()}</span> T</p>
              <p className={clsx('font-semibold', (lastAgg.actual - lastAgg.forecast) < 0 ? 'text-red-600' : 'text-green-600')}>
                Diff: {(lastAgg.actual - lastAgg.forecast) >= 0 ? '+' : ''}{(lastAgg.actual - lastAgg.forecast).toLocaleString()} T
              </p>
            </div>
          ) : (
            <p className="text-sm text-steel-400 mt-1">No earlier month</p>
          )}
        </div>
      </div>

      {/* ===== FILTERS + VIEW TOGGLE ===== */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="flex items-center gap-2 bg-white border border-steel-200 rounded-lg px-3 py-2 flex-1 min-w-48 max-w-xs">
          <Search size={15} className="text-steel-400" />
          <input
            type="text"
            placeholder="Search contractor..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-transparent text-sm outline-none flex-1 text-steel-700 placeholder:text-steel-400"
          />
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="form-input w-auto text-sm"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="need_to_talk">Need to Talk</option>
          <option value="inactive">Inactive</option>
        </select>

        {/* Month filter */}
        <select
          value={selectedMonth}
          onChange={e => setSelectedMonth(e.target.value)}
          className="form-input w-auto text-sm"
        >
          {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        {/* ===== EDIT WHOLE TABLE (bulk edit) ===== */}
        {viewMode === 'table' && (
          bulkEdit ? (
            <div className="flex items-center gap-2">
              <button
                onClick={saveBulk}
                className="flex items-center gap-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg shadow-sm"
              >
                <Check size={16} /> Save
              </button>
              <button
                onClick={cancelBulk}
                className="flex items-center gap-2 text-sm font-medium text-steel-600 bg-steel-100 hover:bg-steel-200 px-4 py-2 rounded-lg"
              >
                <X size={16} /> Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={startBulkEdit}
              className="flex items-center gap-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 px-4 py-2 rounded-lg shadow-sm"
              title="Edit every row and month at once"
            >
              <Edit3 size={16} /> Edit Table
            </button>
          )
        )}

        {/* View mode toggle */}
        <div className="flex items-center bg-steel-100 rounded-lg p-1 ml-auto">
          <button
            onClick={() => setViewMode('table')}
            className={clsx('px-3 py-1 rounded text-xs font-medium transition-colors',
              viewMode === 'table' ? 'bg-white text-steel-800 shadow-sm' : 'text-steel-500')}
          >
            Table
          </button>
          <button
            onClick={() => setViewMode('chart')}
            className={clsx('px-3 py-1 rounded text-xs font-medium transition-colors',
              viewMode === 'chart' ? 'bg-white text-steel-800 shadow-sm' : 'text-steel-500')}
          >
            Chart
          </button>
        </div>
      </div>

      {/* ===== CHART VIEW ===== */}
      {viewMode === 'chart' && (
        <div className="card">
          <h3 className="font-semibold text-steel-800 mb-4">
            Forecast vs Actual Gap by Contractor (Top 10 by Gap)
          </h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" angle={-40} textAnchor="end" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(val: number) => [`${val} T`]}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <Legend />
              <Bar dataKey="forecast" name="Forecast (T)" fill="#C41E3A" radius={[3,3,0,0]} />
              <Bar dataKey="actual"   name="Actual (T)"   fill="#2E7D32" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ===== TABLE VIEW ===== */}
      {viewMode === 'table' && (
        <div className="card p-0 overflow-hidden">
          {/* Horizontal scroll wrapper for wide table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                {/* ===== HEADER ROW 1: Month groups ===== */}
                <tr className="bg-steel-800 text-white">
                  <th className="px-4 py-3 text-left text-xs font-semibold w-10">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold min-w-40">Contractor</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold">Total (T)</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold">Status</th>
                  {/* Month group headers */}
                  {visibleMonths.map(m => (
                    <th
                      key={m}
                      colSpan={3}
                      className={clsx(
                        'px-2 py-3 text-center text-xs font-bold border-l border-steel-600',
                        m === selectedMonth ? 'bg-primary-700' : ''
                      )}
                    >
                      {m}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-semibold border-l border-steel-600">Actions</th>
                </tr>
                {/* ===== HEADER ROW 2: Forecast / Actual / Diff ===== */}
                <tr className="bg-steel-700 text-steel-200">
                  <th colSpan={4} className="px-4 py-2" />
                  {visibleMonths.map(m => (
                    <React.Fragment key={m}>
                      <th className="px-2 py-2 text-center text-xs border-l border-steel-600">Fcst</th>
                      <th className="px-2 py-2 text-center text-xs">Actual</th>
                      <th className="px-2 py-2 text-center text-xs font-bold text-yellow-300">Diff</th>
                    </React.Fragment>
                  ))}
                  <th className="border-l border-steel-600" />
                </tr>
              </thead>

              <tbody className="divide-y divide-steel-100">
                {/* ===== TOP TOTALS ROW — totals shown here so the founder
                     doesn't have to scroll to the bottom of the table ===== */}
                <tr className="bg-steel-700 text-white font-bold">
                  <td colSpan={4} className="px-4 py-2.5 text-sm">
                    TOTAL ({filteredContractors.length} contractors)
                  </td>
                  {visibleMonths.map(month => {
                    const mForecast = filteredContractors.reduce((sum, c) =>
                      sum + (c.monthlyData.find(m => m.month === month)?.forecast || 0), 0)
                    const mActual = filteredContractors.reduce((sum, c) =>
                      sum + (c.monthlyData.find(m => m.month === month)?.actual || 0), 0)
                    const mDiff = mActual - mForecast
                    return (
                      <React.Fragment key={month}>
                        <td className="px-2 py-2.5 text-center text-sm border-l border-steel-600">{mForecast || '—'}</td>
                        <td className="px-2 py-2.5 text-center text-sm">{mActual || '—'}</td>
                        <td className={clsx('px-2 py-2.5 text-center text-sm',
                          mDiff < 0 ? 'text-red-300' : mDiff > 0 ? 'text-green-300' : '')}>
                          {mDiff !== 0 ? mDiff : '—'}
                        </td>
                      </React.Fragment>
                    )
                  })}
                  <td />
                </tr>

                {filteredContractors.map((c) => (
                  <React.Fragment key={c.id}>
                    {/* ===== MAIN DATA ROW ===== */}
                    <tr
                      className={clsx(
                        'table-row-hover',
                        c.status === 'need_to_talk' && 'bg-red-50',
                        expandedRow === c.id && 'bg-blue-50'
                      )}
                    >
                      {/* Sr No */}
                      <td className="table-cell text-center text-steel-400 text-xs">{c.srNo}</td>

                      {/* Contractor Name — editable inline via the pencil */}
                      <td className="table-cell">
                        {editingId === c.id ? (
                          <input
                            type="text"
                            value={editMeta.name}
                            onChange={e => setEditMeta({ ...editMeta, name: e.target.value })}
                            className="w-40 px-1.5 py-0.5 border border-steel-300 rounded text-sm"
                            placeholder="Contractor name"
                          />
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              {c.status === 'need_to_talk' && (
                                <AlertTriangle size={13} className="text-red-500 flex-shrink-0" />
                              )}
                              <span className="font-medium text-steel-800 text-sm">{c.name}</span>
                            </div>
                            {c.notes && (
                              <p className="text-xs text-steel-400 mt-0.5 truncate max-w-36">{c.notes}</p>
                            )}
                          </>
                        )}
                      </td>

                      {/* Total Commitment — editable inline */}
                      <td className="table-cell text-center text-sm font-semibold text-steel-700">
                        {editingId === c.id ? (
                          <input
                            type="number"
                            value={editMeta.totalTon}
                            onChange={e => setEditMeta({ ...editMeta, totalTon: e.target.value })}
                            className="w-16 px-1 py-0.5 border border-steel-300 rounded text-center text-xs"
                            placeholder="0"
                          />
                        ) : (c.totalTon > 0 ? `${c.totalTon.toLocaleString()}` : '—')}
                      </td>

                      {/* Status — editable dropdown inline */}
                      <td className="table-cell text-center">
                        {editingId === c.id ? (
                          <select
                            value={editMeta.status}
                            onChange={e => setEditMeta({ ...editMeta, status: e.target.value as ContractorStatus })}
                            className="px-1 py-1 border border-steel-300 rounded text-xs bg-white"
                          >
                            <option value="active">Active</option>
                            <option value="need_to_talk">Need to Talk</option>
                            <option value="inactive">Inactive</option>
                            <option value="new">New</option>
                          </select>
                        ) : (
                          <span className={STATUS_CONFIG[c.status].className}>
                            {STATUS_CONFIG[c.status].label}
                          </span>
                        )}
                      </td>

                      {/* ===== MONTHLY DATA CELLS ===== */}
                      {visibleMonths.map(month => {
                        const md = c.monthlyData.find(m => m.month === month)
                        // Editable if this single row is being edited OR the whole
                        // table is in bulk-edit mode.
                        const rowEditing = bulkEdit || editingId === c.id
                        const draft = bulkEdit
                          ? (bulkDraft[c.id]?.[month] || { forecast: '', actual: '' })
                          : (editDraft[month] || { forecast: '', actual: '' })
                        const fVal = rowEditing ? toNum(draft.forecast) : (md?.forecast ?? 0)
                        const aVal = rowEditing ? toNum(draft.actual) : (md?.actual ?? 0)
                        const diff = aVal - fVal
                        const onF = (v: string) => bulkEdit ? setBulkDraftField(c.id, month, 'forecast', v) : setDraftField(month, 'forecast', v)
                        const onA = (v: string) => bulkEdit ? setBulkDraftField(c.id, month, 'actual', v) : setDraftField(month, 'actual', v)
                        return (
                          <React.Fragment key={month}>
                            {/* Forecast */}
                            <td className="table-cell text-center text-sm border-l border-steel-100">
                              {rowEditing ? (
                                <input
                                  type="number"
                                  value={draft.forecast}
                                  onChange={e => onF(e.target.value)}
                                  className="w-14 px-1 py-0.5 border border-steel-300 rounded text-center text-xs"
                                  placeholder="0"
                                />
                              ) : md?.forecast ? md.forecast : <span className="text-steel-300">—</span>}
                            </td>
                            {/* Actual */}
                            <td className="table-cell text-center text-sm">
                              {rowEditing ? (
                                <input
                                  type="number"
                                  value={draft.actual}
                                  onChange={e => onA(e.target.value)}
                                  className="w-14 px-1 py-0.5 border border-steel-300 rounded text-center text-xs"
                                  placeholder="0"
                                />
                              ) : md?.actual ? md.actual : <span className="text-steel-300">—</span>}
                            </td>
                            {/* Diff — RED if negative (gap), GREEN if positive. Updates live in edit mode. */}
                            <td className="table-cell text-center text-sm font-bold">
                              {(!fVal && !aVal) ? (
                                <span className="text-steel-300">0</span>
                              ) : diff < 0 ? (
                                <span className="text-red-600">{diff}</span>
                              ) : diff > 0 ? (
                                <span className="text-green-600">+{diff}</span>
                              ) : (
                                <span className="text-steel-400">0</span>
                              )}
                            </td>
                          </React.Fragment>
                        )
                      })}

                      {/* Actions */}
                      <td className="table-cell text-center border-l border-steel-100">
                        <div className="flex items-center justify-center gap-1">
                          {editingId === c.id ? (
                            <>
                              {/* SAVE inline edit */}
                              <button
                                onClick={() => saveEdit(c)}
                                className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                                title="Save changes"
                              >
                                <Check size={14} />
                              </button>
                              {/* CANCEL inline edit */}
                              <button
                                onClick={cancelEdit}
                                className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Cancel"
                              >
                                <X size={14} />
                              </button>
                            </>
                          ) : bulkEdit ? (
                            /* Whole table is editing — use the Save/Cancel buttons above the table */
                            <span className="text-xs text-primary-600 font-medium">editing…</span>
                          ) : (
                            <>
                              {/* VIEW chart (already worked) */}
                              <button
                                onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)}
                                className="p-1.5 text-steel-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title="View details"
                              >
                                <Eye size={14} />
                              </button>
                              {/* EDIT row inline (pencil) */}
                              <button
                                onClick={() => startEdit(c)}
                                className="p-1.5 text-steel-500 hover:text-primary-600 hover:bg-primary-50 rounded"
                                title="Edit forecast / actual"
                              >
                                <Edit3 size={14} />
                              </button>
                              {c.status === 'need_to_talk' && (
                                <button
                                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  title="Flag for marketing follow-up"
                                >
                                  <MessageSquare size={14} />
                                </button>
                              )}
                              {/* DELETE contractor */}
                              <button
                                onClick={() => deleteContractor(c)}
                                className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Delete contractor"
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* ===== EXPANDED DETAIL ROW ===== */}
                    {expandedRow === c.id && (
                      <tr>
                        <td colSpan={100} className="bg-blue-50 border-y border-blue-200 px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Monthly breakdown chart */}
                            <div className="md:col-span-2">
                              <h4 className="text-sm font-semibold text-steel-700 mb-2">
                                Monthly Forecast vs Actual — {c.name}
                              </h4>
                              <ResponsiveContainer width="100%" height={150}>
                                <BarChart
                                  data={c.monthlyData.filter(m => m.forecast > 0 || m.actual > 0)}
                                  margin={{ top: 0, right: 5, left: 0, bottom: 0 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                  <YAxis tick={{ fontSize: 10 }} />
                                  <Tooltip formatter={(v: number) => [`${v} T`]} />
                                  <Bar dataKey="forecast" name="Forecast" fill="#C41E3A" radius={[2,2,0,0]} />
                                  <Bar dataKey="actual"   name="Actual"   fill="#2E7D32" radius={[2,2,0,0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                            {/* Summary stats */}
                            <div className="space-y-3">
                              <div className="bg-white rounded-lg p-3">
                                <p className="text-xs text-steel-500">Annual Commitment</p>
                                <p className="text-xl font-bold text-steel-800">{c.totalTon || '—'} T</p>
                              </div>
                              <div className="bg-white rounded-lg p-3">
                                <p className="text-xs text-steel-500">YTD Forecast</p>
                                <p className="text-lg font-bold text-steel-800">{c.totalForecast} T</p>
                              </div>
                              <div className="bg-white rounded-lg p-3">
                                <p className="text-xs text-steel-500">YTD Actual</p>
                                <p className="text-lg font-bold text-green-700">{c.totalActual} T</p>
                              </div>
                              <div className="bg-white rounded-lg p-3">
                                <p className="text-xs text-steel-500">Total Gap</p>
                                <p className={clsx('text-lg font-bold', c.totalDiff < 0 ? 'text-red-600' : 'text-green-600')}>
                                  {c.totalDiff < 0 ? '' : '+'}{c.totalDiff} T
                                </p>
                              </div>
                              {/* Status + action button */}
                              {c.status === 'need_to_talk' && (
                                <button className="w-full btn-primary text-xs flex items-center justify-center gap-2">
                                  <MessageSquare size={13} />
                                  Send Follow-up to Marketing
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>

              {/* ===== TOTALS FOOTER ROW ===== */}
              <tfoot>
                <tr className="bg-steel-800 text-white font-bold">
                  <td colSpan={4} className="px-4 py-3 text-sm">
                    TOTAL ({filteredContractors.length} contractors)
                  </td>
                  {visibleMonths.map(month => {
                    const mForecast = filteredContractors.reduce((sum, c) => {
                      const md = c.monthlyData.find(m => m.month === month)
                      return sum + (md?.forecast || 0)
                    }, 0)
                    const mActual = filteredContractors.reduce((sum, c) => {
                      const md = c.monthlyData.find(m => m.month === month)
                      return sum + (md?.actual || 0)
                    }, 0)
                    const mDiff = mActual - mForecast
                    return (
                      <React.Fragment key={month}>
                        <td className="px-2 py-3 text-center text-sm border-l border-steel-600">
                          {mForecast || '—'}
                        </td>
                        <td className="px-2 py-3 text-center text-sm">
                          {mActual || '—'}
                        </td>
                        <td className={clsx('px-2 py-3 text-center text-sm',
                          mDiff < 0 ? 'text-red-300' : mDiff > 0 ? 'text-green-300' : ''
                        )}>
                          {mDiff !== 0 ? mDiff : '—'}
                        </td>
                      </React.Fragment>
                    )
                  })}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ===== TABLE FOOTER INFO ===== */}
          <div className="px-4 py-3 bg-steel-50 border-t border-steel-100 flex items-center justify-between text-xs text-steel-500">
            <span>Showing {filteredContractors.length} of {contractors.length} contractors</span>
            <span className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-red-100 border border-red-300 rounded" />
                Negative Diff = Gap (needs follow-up)
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-red-50 border border-red-200 rounded" />
                Need to Talk = Urgent escalation
              </span>
            </span>
          </div>
        </div>
      )}

      {/* ===== ADD CONTRACTOR MODAL ===== */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowAddModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-steel-800">Add Contractor</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-steel-400 hover:text-steel-700 text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Contractor name */}
            <div>
              <label className="text-xs font-medium text-steel-600">Contractor Name *</label>
              <input
                type="text"
                value={addForm.name}
                onChange={e => setAddForm({ ...addForm, name: e.target.value })}
                placeholder="e.g. SP SINGLA"
                className="form-input w-full mt-1"
                autoFocus
              />
            </div>

            {/* Annual commitment + status */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-steel-600">Annual Commitment (T)</label>
                <input
                  type="number"
                  value={addForm.totalTon}
                  onChange={e => setAddForm({ ...addForm, totalTon: e.target.value })}
                  placeholder="0"
                  className="form-input w-full mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-steel-600">Status</label>
                <select
                  value={addForm.status}
                  onChange={e => setAddForm({ ...addForm, status: e.target.value as ContractorStatus })}
                  className="form-input w-full mt-1"
                >
                  <option value="active">Active</option>
                  <option value="need_to_talk">Need to Talk</option>
                  <option value="inactive">Inactive</option>
                  <option value="new">New</option>
                </select>
              </div>
            </div>

            {/* One month's forecast / actual (Diff auto-calculates) */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-steel-600">Month</label>
                <select
                  value={addForm.month}
                  onChange={e => setAddForm({ ...addForm, month: e.target.value })}
                  className="form-input w-full mt-1"
                >
                  {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-steel-600">Forecast (T)</label>
                <input
                  type="number"
                  value={addForm.forecast}
                  onChange={e => setAddForm({ ...addForm, forecast: e.target.value })}
                  placeholder="0"
                  className="form-input w-full mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-steel-600">Actual (T)</label>
                <input
                  type="number"
                  value={addForm.actual}
                  onChange={e => setAddForm({ ...addForm, actual: e.target.value })}
                  placeholder="0"
                  className="form-input w-full mt-1"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowAddModal(false)} className="btn-secondary text-sm">
                Cancel
              </button>
              <button onClick={handleAddContractor} className="btn-primary text-sm">
                Add Contractor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DemandForecast
